<template>
  <div class="recommended-products">
    <div class="recommended-products__header">
      <h2
        v-if="liquid.title"
        id="section-title-{{ section.id }}"
        class="
          text-heading-2xl
          text-heading-4xl-desktop
          recommended-products__title
        "
        v-text="liquid.title"
      />

      <p
        v-if="liquid.subcopy"
        class="recommended-products__subcopy text-body-xl-regular mt-xl"
        v-text="liquid.subcopy"
      />
    </div>

    <div class="recommended-products__filters">
      <button
        class="recommended-products__filter"
        :class="!currentFilter ? 'is-active' : ''"
        type="button"
        @click="handleFilterSelection()"
      >
        <span
          class="text-body-l"
          v-text="$string('product.filters_all')"
        />
      </button>

      <button
        v-for="(filter, index) of liquid.filters"
        :key="index"
        class="recommended-products__filter"
        :class="currentFilter === filter ? 'is-active' : ''"
        type="button"
        @click="handleFilterSelection(filter)"
      >
        <span
          class=" text-body-l"
          v-text="filter"
        />
      </button>
    </div>

    <splide
      ref="splide"
      class="
        mt-2xl
        recommended-products__carousel
      "
      :has-track="false"
      :options="config"
    >
      <splide-track>
        <splide-slide
          v-for="(product, index) of productList"
          :key="`slide-${index}`"
        >
          <product-card
            :product="product"
            :loading="false"
            :sku="true"
            :plp="true"
            @display-options="getProductVariants(product)"
          />
        </splide-slide>
      </splide-track>
      <div class="text-center mt-2xl mb-2xl">
        <splide-arrows
          v-show="pageCount > 1"
          block-class="recommended-products"
          :curent-index="currentIndex"
          :page-count="pageCount"
        />
      </div>
    </splide>

    <div
      class="
        recommended-products__footer
        recommended-products__footer
        mt-2xl
        text-center"
    >
      <btn
        v-if="liquid.button_url"
        :url="liquid.button_url"
        :label="liquid.button_text"
        :modifiers="liquid.button_style"
        :show-icon="false"
        class="recommended-products__cta"
      />
    </div>

    <product-details-drawer
      v-if="productDrawer"
      :product="productDrawer"
      :selected-variant="selectedVariant"
    />
  </div>
</template>

<script>

/**
 * Vue: Recommended products (recommended-products)
 * -----------------------------------------------------------------------------
 * Cross sell module to recommend products.
 * - Limited interactivity section.
 *
 * @param {String} liquid.sectionId - Section's Liquid ID.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'
import Btn from '~global/btn/btn'
import ProductCard from '~async/product-card/product-card'
import ProductDetailsDrawer from '~async/product-details-drawer/product-details-drawer'
import productQuery from '~graphql/product.gql'
import SplideArrows from '~global/splide-arrows/splide-arrows'
import { formatProduct, formatUrl } from '~/helpers/format'
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide'
import { values as breakpoints } from '~/config/breakpoints'
import { debounce } from '~/helpers/general'
export default {
  name: 'RecommendedProducts',

  components: {
    Splide,
    SplideArrows,
    SplideSlide,
    SplideTrack,
    ProductDetailsDrawer,
    ProductCard,
    Btn,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },
  },

  data() {
    return {
      productDrawer: false,
      currentFilter: '',
      currentIndex: 1,
      cacheIndex: 0,
      offSetDesktopIndex: 0,
      pageCount: 0,
      productList: this.liquid.products || [],
      config: {
        gap: 24,
        perPage: 3,
        pagination: false,
        focus: 0,
        omitEnd: true,
        breakpoints: {
          [breakpoints.m]: {
            perPage: 1.125,
            focus: 0,
          },
        },
      },
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      width: (state) => state.index.screen.width,
    }),

    isTablet() {
      return this.width > 768
    },
  },
  watch: {

    getActiveOverlay() {
      if (!this.getActiveOverlay) {
        this.productDrawer = false
      }
    },

    productDrawer() {
      if (!this.productDrawer) {
        return
      }

      this.selectedVariant = this.productDrawer.variants[0]
    },
  },

  mounted() {
    this.screen = {
      height: window.visualViewport.height,
      width: window.innerWidth,
    }
    this.offSetDesktopIndex = this.productList.length >= this.config.perPage
      ? this.config.perPage
      : this.productList.length
    this.buildPaginationCount(this.productList)
    this.setEventListeners()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openOverlay: 'overlays/open',
      closeOverlay: 'overlays/close',
    }),

    /**
     * Handle filter tab click event
     */
    handleFilterSelection(filter) {
      this.currentFilter = filter
      this.updateProductList(filter)
      this.buildPaginationCount(this.productList)
    },

    /**
     * Update product list with new filter selection
     */
    updateProductList(filter) {
      if (!filter) {
        this.productList = this.liquid.products
        return
      }

      this.productList = this.liquid.products.filter((product) => product.tags.includes(`__filterCategory:${filter}`))
    },

    setEventListeners() {
      this.$refs.splide.splide.on('moved', (index) => {
        this.cacheIndex = index
        this.updateCurrentIndex()
      })

      window.visualViewport.addEventListener('resize', debounce(() => {
        this.updateCurrentIndex()
      }, 300), { passive: true })
    },

    /**
     * Update current display index
     */
    updateCurrentIndex() {
      if (!this.isTablet) {
        this.currentIndex = this.cacheIndex + 1
        return
      }

      if (this.cacheIndex + this.offSetDesktopIndex > this.productList.length) {
        this.currentIndex = this.productList.length
        return
      }

      this.currentIndex = this.cacheIndex + this.offSetDesktopIndex
    },

    /**
     * Build pagination count
     */
    buildPaginationCount(productList) {
      this.pageCount = productList.length
      this.updateCurrentIndex()
    },

    async getProductVariants(product) {
      const { country, language } = window.cnvs.store
      const savedVariant = this.liquid.products.find((item) => item.handle === product.handle)

      if (savedVariant) {
        this.productDrawer = savedVariant
        this.drawerModel = product.compatibility
        return
      }

      try {
        const response = await cnvs.Query({
          query: productQuery,
          variables: {
            country,
            handle: product.handle,
            language,
          },
        })

        const formattedProduct = formatProduct(response)
        this.productDrawer = formattedProduct
        this.viewedProducts.push(formattedProduct)
        this.drawerModel = product.compatibility

      } catch (error) {
        this.productDrawer = false
        window.location = formatUrl(product.url)
        cnvs.ReportError('Failed to fetch products', error)
      }
    },
  },
}
</script>

<style lang="scss">
@import './recommended-products';
</style>
